import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, ArrowRight, ChevronDown } from "lucide-react";
import axios from "axios";

const API = "/api";

/* Saffron accent for Icons section — distinct from main amber */
const SAFFRON = "#C99A2E";
const SAFFRON_LIGHT = "#F2E8DD";
const IVORY = "#FEFCF7";
const INK = "#1A1A18";
const INK_MID = "#3D3D38";
const INK_SOFT = "#6B6B63";

function getInitials(name) {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function IconCard({ icon, featured }) {
  const hasPortrait = icon.portrait_url && icon.portrait_url.length > 5;

  if (featured) {
    return (
      <Link
        to={`/icons/${icon.slug}`}
        data-testid={`icon-card-${icon.slug}`}
        className="group col-span-full block relative overflow-hidden"
        style={{
          borderRadius: '20px',
          background: `linear-gradient(135deg, ${INK} 0%, #2A2520 100%)`,
          minHeight: '340px',
        }}
      >
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-14 p-8 md:p-14">
          {/* Portrait */}
          <div className="flex-shrink-0">
            {hasPortrait ? (
              <img
                src={icon.portrait_url}
                alt={icon.name}
                className="iab-bw-portrait w-40 h-40 md:w-52 md:h-52 rounded-full object-cover border-4"
                style={{ borderColor: '#C9A24B' }}
              />
            ) : (
              <div
                className="w-40 h-40 md:w-52 md:h-52 rounded-full flex items-center justify-center text-4xl font-semibold border-4"
                style={{ background: 'rgba(201,154,46,0.15)', color: SAFFRON, borderColor: SAFFRON, fontFamily: "var(--iab-font-accent)" }}
              >
                {getInitials(icon.name)}
              </div>
            )}
          </div>
          {/* Info */}
          <div className="flex-1 text-center md:text-left">
            <p className="uppercase text-xs tracking-widest mb-3 font-semibold" style={{ color: SAFFRON }}>
              Guiding Light
            </p>
            <h2 className="text-3xl md:text-4xl font-medium mb-3" style={{ color: '#FEFCF7', fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '-0.5px' }}>
              {icon.name}
            </h2>
            <p className="text-base mb-5" style={{ color: 'rgba(254,252,247,0.65)', fontFamily: "var(--iab-font-accent)", fontStyle: 'italic', lineHeight: 1.7, maxWidth: '520px' }}>
              {icon.title_line}
            </p>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(254,252,247,0.5)', maxWidth: '560px' }}>
              {icon.bio_short}
            </p>
            <span
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold transition-all group-hover:scale-105"
              style={{ background: SAFFRON, color: '#fff' }}
            >
              Explore Legacy <ArrowRight size={16} />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/icons/${icon.slug}`}
      data-testid={`icon-card-${icon.slug}`}
      className="group block"
      style={{ borderRadius: '16px', overflow: 'hidden', background: '#fff', border: '0.5px solid rgba(26,26,24,0.08)', transition: 'all 0.25s ease' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(26,26,24,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Portrait */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '1', background: SAFFRON_LIGHT }}>
        {hasPortrait ? (
          <img
            src={icon.portrait_url}
            alt={icon.name}
            className="iab-bw-portrait w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${SAFFRON_LIGHT}, #FFF5EB)` }}>
            <span className="text-5xl font-medium" style={{ color: SAFFRON, fontFamily: "var(--iab-font-accent)", opacity: 0.6 }}>
              {getInitials(icon.name)}
            </span>
          </div>
        )}
        {/* Category pill */}
        {icon.category && icon.category[0] && (
          <span
            className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(255,255,255,0.9)', color: INK_MID, backdropFilter: 'blur(8px)' }}
          >
            {icon.category[0]}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="p-5">
        <h3 className="text-base font-semibold mb-1" style={{ color: INK, fontFamily: "'Playfair Display', Georgia, serif" }}>
          {icon.name}
        </h3>
        <p className="text-xs mb-3" style={{ color: SAFFRON, fontFamily: "var(--iab-font-accent)", fontStyle: 'italic', letterSpacing: '0.3px' }}>
          {icon.title_line}
        </p>
        <p className="text-sm leading-relaxed" style={{ color: INK_SOFT, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {icon.bio_short}
        </p>
        {icon.episodes && icon.episodes.length > 0 && (
          <p className="mt-3 text-xs font-medium" style={{ color: SAFFRON }}>
            {icon.episodes.length} episode{icon.episodes.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </Link>
  );
}

export default function IconsPage() {
  const [icons, setIcons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/icons`),
      axios.get(`${API}/icons/categories`),
    ]).then(([iconsRes, catsRes]) => {
      setIcons(iconsRes.data);
      setCategories(catsRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const anchor = icons.find(i => i.is_anchor);
  const regularIcons = icons.filter(i => !i.is_anchor);

  const filtered = regularIcons.filter(icon => {
    const matchSearch = !search || icon.name.toLowerCase().includes(search.toLowerCase()) || icon.title_line.toLowerCase().includes(search.toLowerCase());
    const matchCategory = !categoryFilter || (icon.category && icon.category.includes(categoryFilter));
    return matchSearch && matchCategory;
  });

  return (
    <div data-testid="icons-page" className="min-h-screen" style={{ background: IVORY }}>
      {/* Hero */}
      <div style={{ background: `linear-gradient(160deg, ${IVORY} 0%, ${SAFFRON_LIGHT} 100%)`, paddingTop: '48px', paddingBottom: '56px' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="uppercase mb-5" style={{ fontSize: '11px', letterSpacing: '3px', fontWeight: 600, color: SAFFRON }}>
            The Icons Collection
          </p>
          <h1 className="mb-5" style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 500, color: INK, letterSpacing: '-0.5px', lineHeight: 1.15 }}>
            I am because{' '}
            <em style={{ color: SAFFRON }}>they were</em>
          </h1>
          <p className="max-w-xl" style={{ fontFamily: "var(--iab-font-accent)", fontSize: '17px', lineHeight: 1.8, color: INK_SOFT, fontStyle: 'italic' }}>
            Every person here has a why. These are the whys that built the India we live in.
          </p>
          <div className="mt-6" style={{ width: '56px', height: '3px', background: SAFFRON }} />
        </div>
      </div>

      {/* Filter Bar */}
      <div className="sticky top-[72px] z-30 border-b" style={{ background: 'rgba(254,252,247,0.92)', backdropFilter: 'blur(12px)', borderColor: 'rgba(26,26,24,0.08)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-3 flex items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: INK_SOFT }} />
            <input
              data-testid="icons-search"
              type="text"
              placeholder="Search icons..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border outline-none transition-colors"
              style={{ borderColor: 'rgba(26,26,24,0.12)', background: '#fff', color: INK }}
            />
          </div>
          {/* Category Filter */}
          <div className="relative">
            <button
              data-testid="icons-category-filter"
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors"
              style={{ borderColor: categoryFilter ? SAFFRON : 'rgba(26,26,24,0.12)', color: categoryFilter ? SAFFRON : INK_SOFT, background: '#fff' }}
            >
              <Filter size={14} />
              {categoryFilter || "Category"}
              <ChevronDown size={14} />
            </button>
            {showFilterDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-white rounded-xl border shadow-lg py-1 z-50 w-48" style={{ borderColor: 'rgba(26,26,24,0.08)' }}>
                <button
                  onClick={() => { setCategoryFilter(""); setShowFilterDropdown(false); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                  style={{ color: !categoryFilter ? SAFFRON : INK_SOFT, fontWeight: !categoryFilter ? 600 : 400 }}
                >
                  All Categories
                </button>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => { setCategoryFilter(cat); setShowFilterDropdown(false); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                    style={{ color: categoryFilter === cat ? SAFFRON : INK_MID, fontWeight: categoryFilter === cat ? 600 : 400 }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: SAFFRON, borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Sam Pitroda featured card — full width */}
            {anchor && !search && !categoryFilter && (
              <IconCard icon={anchor} featured />
            )}

            {/* Regular icons grid */}
            {filtered.map(icon => (
              <IconCard key={icon.id} icon={icon} />
            ))}

            {filtered.length === 0 && !loading && (
              <div className="col-span-full text-center py-16">
                <p className="text-lg mb-2" style={{ color: INK_MID }}>No icons found</p>
                <p className="text-sm" style={{ color: INK_SOFT }}>Try a different search or category</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* About section */}
      <div style={{ background: `linear-gradient(135deg, ${SAFFRON_LIGHT}, #FFF8F0)`, borderTop: `0.5px solid rgba(201,154,46,0.15)` }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 text-center">
          <p className="uppercase text-xs tracking-widest mb-4 font-semibold" style={{ color: SAFFRON }}>About This Project</p>
          <p style={{ fontFamily: "var(--iab-font-accent)", fontSize: '17px', lineHeight: 1.9, color: INK_MID, fontStyle: 'italic' }}>
            "Icons of India" is dedicated to revealing the untold stories of India's pivotal figures — those whose vision and actions helped transform India into a global powerhouse. It explores the significant contributions of leaders from various sectors that are not typically covered in school curriculums, providing inspiration and education for future generations.
          </p>
          <div className="flex items-center justify-center gap-8 mt-8 text-xs" style={{ color: INK_SOFT }}>
            <span>Co-Host: Dipali Sikand</span>
            <span>Coordinator: Rakesh Patel</span>
            <span>Design: Rajeev Kumar</span>
          </div>
        </div>
      </div>
    </div>
  );
}
